import React, { useState, useMemo } from 'react';

// ==================== Categories & Commodities ====================
const categories = [
  // Agri & Food Processing
  { id: 'millets', name: 'Millets & Cereals 🌾', examples: ['Jowar 🌾', 'Bajra 🌱', 'Ragi 🌾', 'Rice 🍚', 'Wheat 🌾'] },
  { id: 'pulses', name: 'Pulses 🥘', examples: ['Chana 🟡', 'Tur Dal 🟠', 'Moong 🟢', 'Urad ⚫'] },
  { id: 'oilseeds', name: 'Oilseeds 🛢️', examples: ['Groundnut 🥜', 'Sesame 🌾', 'Sunflower 🌻'] },
  { id: 'fruits', name: 'Fruits 🍎', examples: ['Mango 🥭', 'Banana 🍌', 'Papaya 🥭', 'Pomegranate 🍎'] },
  { id: 'vegetables', name: 'Vegetables 🥦', examples: ['Tomato 🍅', 'Onion 🧅', 'Okra 🌿', 'Brinjal 🍆'] },
  { id: 'spices', name: 'Spices 🧂', examples: ['Turmeric 🌿', 'Cumin 🌾', 'Coriander 🌿', 'Chili 🌶️'] },
  { id: 'processed', name: 'Processed Foods 🥫', examples: ['Millet Flour 🌾', 'Pickles 🥒', 'Fruit Pulp 🍑', 'Cold-Pressed Oils 🛢️'] },
  // Dairy & Animal Products
  { id: 'dairy', name: 'Dairy & Livestock 🐄', examples: ['Milk 🥛', 'Paneer 🧀', 'Ghee 🧈', 'Eggs 🥚', 'Honey 🍯'] },
  // Herbal & Wellness
  { id: 'herbal', name: 'Herbal & Wellness 🌿', examples: ['Tulsi 🌿', 'Aloe Vera 🌱', 'Moringa 🌱', 'Turmeric Powder 🌿'] },
  // Handicrafts / Handloom
  { id: 'handicraft', name: 'Handicrafts & Textiles 🧵', examples: ['Sarees 👗', 'Dupattas 🧣', 'Bedcovers 🛏️', 'Jute Bags 🛍️', 'Paper Crafts 📜'] },
  // Beauty / Fashion Accessories
  { id: 'fashion', name: 'Fashion Accessories 💍', examples: ['Jewelry 💎', 'Scarves 🧣', 'Handbags 👜', 'Belts 👝'] },
  // Eco / Upcycled
  { id: 'eco', name: 'Eco / Upcycled ♻️', examples: ['Straw Bags 👜', 'Recycled Textiles 👕', 'Cow-dung Idols 🪔', 'Natural Incense 🌿'] },
  // Fisheries
  { id: 'fisheries', name: 'Fisheries & Aquaculture 🐟', examples: ['Shrimp 🍤', 'Rohu 🐟', 'Catla 🐟', 'Tilapia 🐠'] },
  // Seasonal & Festival Products
  { id: 'festival', name: 'Seasonal / Festival 🎉', examples: ['Rakhis 🎁', 'Idols 🪔', 'Decorative Diyas 🪔', 'Gift Hampers 🎁'] },
];

// ==================== Tabs and Actions ====================
const MAIN_TABS = ['Primary', 'Secondary', 'Tertiary', 'Innovation'];
const ACTIONS = ['Bulk Sell', 'Future Order', 'Price List', 'Suppliers'];

// ==================== Packaging Toolkit ====================
const packagingItems = {
  millets: { primary: 'PP Bags / Jute Bags 🛍️', secondary: 'Cardboard Boxes 📦', tertiary: 'Wooden Crates 🏗️' },
  pulses: { primary: 'Poly Bags 🛍️', secondary: 'Corrugated Boxes 📦', tertiary: 'Wooden Crates 🏗️' },
  oilseeds: { primary: 'Jute Bags 🛍️', secondary: 'Fiber Drums 📦', tertiary: 'Wooden Pallets 🏗️' },
  fruits: { primary: 'Plastic Trays / Banana Leaves 🍌', secondary: 'Cartons 📦', tertiary: 'Wooden Crates 🏗️' },
  vegetables: { primary: 'Plastic Trays / Basket Packaging 🥦', secondary: 'Cartons 📦', tertiary: 'Wooden Crates 🏗️' },
  spices: { primary: 'Glass / Plastic Jars 🧂', secondary: 'Cardboard Boxes 📦', tertiary: 'Bulk Containers 🏗️' },
  processed: { primary: 'Plastic Pouches / Cloth Bags 🥫', secondary: 'Cartons 📦', tertiary: 'Wooden Pallets 🏗️' },
  dairy: { primary: 'Milk Pouches 🥛', secondary: 'Cartons 📦', tertiary: 'Cold Storage 🏗️' },
  herbal: { primary: 'Glass Jars 🌿', secondary: 'Cartons 📦', tertiary: 'Bulk Containers 🏗️' },
  handicraft: { primary: 'Cloth / Jute Bags 👜', secondary: 'Cardboard Boxes 📦', tertiary: 'Wooden Crates 🏗️' },
  fashion: { primary: 'Boxes / Cloth Bags 👜', secondary: 'Cartons 📦', tertiary: 'Pallets 🏗️' },
  eco: { primary: 'Straw Bags 👜', secondary: 'Cartons 📦', tertiary: 'Bulk Containers 🏗️' },
  fisheries: { primary: 'Ice-packed Bags 🐟', secondary: 'Styrofoam Boxes 📦', tertiary: 'Refrigerated Containers 🏗️' },
  festival: { primary: 'Gift Boxes 🎁', secondary: 'Cartons 📦', tertiary: 'Wooden Crates 🏗️' },
};

// ==================== Dummy Data for Actions ====================
const actionData = {
  'Bulk Sell': (commodity, packaging) => ({
    volume: (() => {
      switch (commodity) {
        case 'Jowar 🌾': return '200-600 kg';
        case 'Chana 🟡': return '150-400 kg';
        case 'Mango 🥭': return '100-300 kg';
        case 'Milk 🥛': return '50-200 L';
        case 'Sarees 👗': return '30-100 pcs';
        case 'Jewelry 💎': return '50-200 pcs';
        default: return '100-500 kg/unit';
      }
    })(),
    packaging: packaging.primary,
    note: `Ideal for SHGs/FPOs in Maharashtra selling ${commodity}.`
  }),

  'Future Order': (commodity, packaging) => ({
    schedule: (() => {
      switch (commodity) {
        case 'Ragi 🌾':
        case 'Tur Dal 🟠':
        case 'Groundnut 🥜':
          return 'Next 3-5 weeks';
        case 'Papaya 🥭':
        case 'Tomato 🍅':
          return 'Next 1-2 weeks';
        case 'Honey 🍯':
        case 'Tulsi 🌿':
          return 'Next 4 weeks';
        default:
          return 'Next 2-4 weeks';
      }
    })(),
    packaging: packaging.primary,
    note: `Schedule future orders of ${commodity} with SHG packaging support.`
  }),

  'Price List': (commodity, packaging) => ({
    priceRange: (() => {
      switch (commodity) {
        case 'Jowar 🌾': return '₹40-50 per kg';
        case 'Chana 🟡': return '₹80-100 per kg';
        case 'Mango 🥭': return '₹60-150 per kg';
        case 'Milk 🥛': return '₹50-60 per L';
        case 'Sarees 👗': return '₹500-1500 per piece';
        case 'Jewelry 💎': return '₹200-800 per piece';
        default: return '₹150-500 per kg/unit';
      }
    })(),
    packaging: packaging.primary,
    note: `Indicative price for women-led enterprise products of ${commodity}.`
  }),

  'Suppliers': (commodity, packaging) => ({
    suppliers: (() => {
      switch (commodity) {
        case 'Jowar 🌾':
        case 'Ragi 🌾':
          return ['SHG Satara', 'FPO Ahmednagar'];
        case 'Chana 🟡':
        case 'Tur Dal 🟠':
          return ['SHG Pune', 'Women SHG Nashik'];
        case 'Mango 🥭':
        case 'Papaya 🥭':
          return ['FPO Konkan', 'SHG Ratnagiri'];
        case 'Sarees 👗':
        case 'Dupattas 🧣':
          return ['Women Weaver SHG Nagpur', 'FPO Wardha'];
        case 'Milk 🥛':
        case 'Paneer 🧀':
          return ['Dairy SHG Satara', 'FPO Kolhapur'];
        default:
          return ['SHG Satara', 'FPO Ahmednagar', 'Women SHG Konkan'];
      }
    })(),
    packaging: packaging.primary,
    note: `Local supplier network for ${commodity} in Maharashtra.`
  })
};

// ==================== Main Component ====================
export default function MahilaMandiPackaging() {
  const [mainTab, setMainTab] = useState(MAIN_TABS[0]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(categories[0].id);
  const selectedCategory = useMemo(() => categories.find(c => c.id === selectedCategoryId), [selectedCategoryId]);
  const [selectedCommodity, setSelectedCommodity] = useState(selectedCategory.examples[0]);
  const [selectedAction, setSelectedAction] = useState(ACTIONS[0]);

  const currentPackaging = packagingItems[selectedCategoryId] || { primary: '', secondary: '', tertiary: '' };
  const currentActionData = actionData[selectedAction](selectedCommodity, currentPackaging);

  return (
    <div className="min-h-screen bg-gray-900 text-yellow-200 p-4 flex justify-center">
      <div className="w-full rounded-2xl shadow-lg bg-gray-800 p-4">
        <h1 className="text-2xl font-bold text-center mb-4">MahilaMandi Packaging Hub</h1>

        {/* Main Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-4 border-b py-2 border-white place-items-center">
          {MAIN_TABS.map(t => (
            <button
              key={t}
              onClick={() => { setMainTab(t); setSelectedAction(ACTIONS[0]); setSelectedCategoryId(categories[0].id); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                mainTab === t ? 'bg-yellow-600 text-black shadow-md' : 'bg-yellow-700 text-gray-200 hover:bg-yellow-500'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Innovation / Packaging Toolkit Section */}
        {mainTab === 'Innovation' && (
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-2 text-center">Packaging Toolkit & Innovations</h2>
            <div className="space-y-2 mb-4">
              <div className="bg-gray-700 p-3 rounded-lg shadow-md">
                <h3 className="font-semibold">Primary Packaging</h3>
                <p>{currentPackaging.primary}</p>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg shadow-md">
                <h3 className="font-semibold">Secondary Packaging</h3>
                <p>{currentPackaging.secondary}</p>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg shadow-md">
                <h3 className="font-semibold">Tertiary Packaging</h3>
                <p>{currentPackaging.tertiary}</p>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-2 border-b border-white place-items-center">
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => { setSelectedCategoryId(c.id); setSelectedCommodity(c.examples[0]); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategoryId === c.id ? 'bg-yellow-600 text-black' : 'bg-yellow-700 text-gray-200 hover:bg-yellow-500'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Commodities */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-2 py-2 border-b border-green-400 place-items-center">
          {selectedCategory.examples.map(ex => (
            <button
              key={ex}
              onClick={() => setSelectedCommodity(ex)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCommodity === ex ? 'bg-yellow-600 text-black' : 'bg-yellow-700 text-gray-200 hover:bg-yellow-500'
              }`}
            >
              {ex}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-4 place-items-center">
          {ACTIONS.map(a => (
            <button
              key={a}
              onClick={() => setSelectedAction(a)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedAction === a ? 'bg-yellow-600 text-black' : 'bg-yellow-700 text-gray-200 hover:bg-yellow-500'
              }`}
            >
              {a}
            </button>
          ))}
        </div>

        {/* Display Action Data */}
        <div className="bg-gray-700 p-3 rounded-lg shadow-md space-y-2">
          <h3 className="font-semibold">{selectedAction} Info for {selectedCommodity}</h3>
          {selectedAction === 'Bulk Sell' && (
            <p>Volume: {currentActionData.volume}<br/>Packaging: {currentActionData.packaging}<br/>Note: {currentActionData.note}</p>
          )}
          {selectedAction === 'Future Order' && (
            <p>Schedule: {currentActionData.schedule}<br/>Packaging: {currentActionData.packaging}<br/>Note: {currentActionData.note}</p>
          )}
          {selectedAction === 'Price List' && (
            <p>Price Range: {currentActionData.priceRange}<br/>Packaging: {currentActionData.packaging}<br/>Note: {currentActionData.note}</p>
          )}
          {selectedAction === 'Suppliers' && (
            <p>Suppliers: {currentActionData.suppliers.join(', ')}<br/>Packaging: {currentActionData.packaging}<br/>Note: {currentActionData.note}</p>
          )}
        </div>
      </div>
    </div>
  );
}
